import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PakistanBankPicker } from "@/components/sales/PakistanBankPicker";
import type { SalePaymentMethod } from "@/lib/sales/salesStore";
import { cn } from "@/lib/utils";

export type PaymentMethodValue = SalePaymentMethod | "";

export type PaymentMethodDraft = {
  paymentMethod: PaymentMethodValue;
  bankName: string;
  transactionReferenceNo: string;
  payOrderNo: string;
  chequeNo?: string;
  depositSlipNo?: string;
};

export function emptyPaymentMethodDraft(
  patch?: Partial<PaymentMethodDraft>,
): PaymentMethodDraft {
  return {
    paymentMethod: "",
    bankName: "",
    transactionReferenceNo: "",
    payOrderNo: "",
    chequeNo: "",
    depositSlipNo: "",
    ...patch,
  };
}

/** Same clear rules as Create Sale Settlement when switching methods. */
export function applyPaymentMethodChange(
  current: PaymentMethodDraft,
  method: PaymentMethodValue,
): PaymentMethodDraft {
  const usesBank = method === "online" || method === "cheque" || method === "pay_order" || method === "cash_deposit";
  return {
    ...current,
    paymentMethod: method,
    bankName: usesBank ? current.bankName : "",
    transactionReferenceNo: method === "online" ? current.transactionReferenceNo : "",
    chequeNo: method === "cheque" ? (current.chequeNo ?? "") : "",
    payOrderNo: method === "pay_order" ? current.payOrderNo : "",
    depositSlipNo: method === "cash_deposit" ? (current.depositSlipNo ?? "") : "",
  };
}

type SelectProps = {
  value: PaymentMethodValue;
  onChange: (method: PaymentMethodValue) => void;
  disabled?: boolean;
  placeholder?: string;
  noneLabel?: string;
  /** When false, hides the "none" entry — the first real method acts as the default. */
  allowNone?: boolean;
  id?: string;
  label?: string;
  labelClassName?: string;
  triggerClassName?: string;
  className?: string;
};

/** Cash / Online / Cheque / Pay Order / Cash Deposit selector used by Settlement and product cost lines. */
export function PaymentMethodSelect({
  value,
  onChange,
  disabled = false,
  placeholder = "Select method (optional)",
  noneLabel = "— None —",
  allowNone = true,
  id,
  label = "Payment method",
  labelClassName = "text-xs text-muted-foreground",
  triggerClassName = "glass-input h-10",
  className,
}: SelectProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id} className={labelClassName}>
        {label}
      </Label>
      <Select
        value={value || (allowNone ? "__none__" : undefined)}
        onValueChange={(v) => onChange(v === "__none__" ? "" : (v as SalePaymentMethod))}
        disabled={disabled}
      >
        <SelectTrigger id={id} className={triggerClassName}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent position="popper">
          {allowNone ? <SelectItem value="__none__">{noneLabel}</SelectItem> : null}
          <SelectItem value="cash">Cash</SelectItem>
          <SelectItem value="online">Online</SelectItem>
          <SelectItem value="cheque">Cheque</SelectItem>
          <SelectItem value="pay_order">Pay Order</SelectItem>
          <SelectItem value="cash_deposit">Cash Deposit</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

type DetailProps = {
  method: PaymentMethodValue;
  bankName: string;
  transactionReferenceNo?: string;
  payOrderNo?: string;
  chequeNo?: string;
  depositSlipNo?: string;
  onBankNameChange: (value: string) => void;
  onTransactionReferenceChange?: (value: string) => void;
  onPayOrderNoChange?: (value: string) => void;
  onChequeNoChange?: (value: string) => void;
  onDepositSlipNoChange?: (value: string) => void;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  className?: string;
};

/** Dynamic bank / reference fields for Online, Cheque, Pay Order, and Cash Deposit. */
export function PaymentMethodDetailFields({
  method,
  bankName,
  transactionReferenceNo = "",
  payOrderNo = "",
  chequeNo = "",
  depositSlipNo = "",
  onBankNameChange,
  onTransactionReferenceChange,
  onPayOrderNoChange,
  onChequeNoChange,
  onDepositSlipNoChange,
  disabled = false,
  labelClassName = "text-xs text-muted-foreground",
  inputClassName = "glass-input h-10",
  className,
}: DetailProps) {
  if (method === "online") {
    return (
      <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2 items-end", className)}>
        <div className="space-y-1 relative z-30">
          <Label className={labelClassName}>Bank name</Label>
          <PakistanBankPicker
            value={bankName}
            onChange={onBankNameChange}
            disabled={disabled}
            inputClassName={inputClassName}
          />
        </div>
        <div className="space-y-1">
          <Label className={labelClassName}>Transaction reference no.</Label>
          <Input
            className={inputClassName}
            value={transactionReferenceNo}
            onChange={(e) => onTransactionReferenceChange?.(e.target.value)}
            placeholder="Optional"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "cheque") {
    return (
      <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2 items-end", className)}>
        <div className="space-y-1 relative z-30">
          <Label className={labelClassName}>Bank name</Label>
          <PakistanBankPicker
            value={bankName}
            onChange={onBankNameChange}
            disabled={disabled}
            inputClassName={inputClassName}
          />
        </div>
        <div className="space-y-1">
          <Label className={labelClassName}>Cheque number</Label>
          <Input
            className={inputClassName}
            value={chequeNo}
            onChange={(e) => onChequeNoChange?.(e.target.value)}
            placeholder="Optional"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "pay_order") {
    return (
      <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2 items-end", className)}>
        <div className="space-y-1 relative z-30">
          <Label className={labelClassName}>Bank name</Label>
          <PakistanBankPicker
            value={bankName}
            onChange={onBankNameChange}
            disabled={disabled}
            inputClassName={inputClassName}
          />
        </div>
        <div className="space-y-1">
          <Label className={labelClassName}>Pay order no.</Label>
          <Input
            className={inputClassName}
            value={payOrderNo}
            onChange={(e) => onPayOrderNoChange?.(e.target.value)}
            placeholder="Optional"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (method === "cash_deposit") {
    return (
      <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2 items-end", className)}>
        <div className="space-y-1 relative z-30">
          <Label className={labelClassName}>Bank name</Label>
          <PakistanBankPicker
            value={bankName}
            onChange={onBankNameChange}
            disabled={disabled}
            inputClassName={inputClassName}
          />
        </div>
        <div className="space-y-1">
          <Label className={labelClassName}>Deposit slip no.</Label>
          <Input
            className={inputClassName}
            value={depositSlipNo}
            onChange={(e) => onDepositSlipNoChange?.(e.target.value)}
            placeholder="Optional"
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  return null;
}
